/**
 * Dietitian Referral Details Page Component
 *
 * Detailed view of a specific referral for dietitian users.
 * Shows comprehensive referral information, patient details, and files.
 *
 * @module components/pages/DieticianReferralDetailsPage
 */

import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTheme } from '@/hooks/useTheme';
import { useDieticianReferralDetails } from '@/hooks/useDieticianReferralDetails';
import { usePatientMealPlanAssignments } from '@/hooks/usePatientMealPlanAssignments';
import { Layout } from '@/components/layout/Layout';
import { TitleText } from '@/components/ui/TitleText';
import { SubtitleText } from '@/components/ui/SubtitleText';
import { BodyText } from '@/components/ui/BodyText';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ReferralStatusBadge } from '@/components/ui/ReferralStatusBadge';
import { AvatarPhoto } from '@/components/ui/AvatarPhoto';
import { FilesCard } from '@/components/dietician-dashboard/FilesCard';
import { AppointmentDisplay } from '@/components/patient-dashboard/AppointmentDisplay';
import { RescheduleAppointmentModal } from '@/components/patient-dashboard/RescheduleAppointmentModal';
import { CancelAppointmentModal } from '@/components/patient-dashboard/CancelAppointmentModal';
import { MedicalConditionTags } from '@/components/ui/MedicalConditionTags';
import { PatientMealPlanCard } from '@/components/dietician-dashboard/PatientMealPlanCard';
import { AssignMealPlanDialog } from '@/components/dietician-dashboard/AssignMealPlanDialog';
import { BackButton } from '../ui/BackButton';
import { PhoneUtil } from '@/utils/PhoneUtil';
import { AppointmentHistoryCard } from '@/components/referrals/AppointmentHistoryCard';
import { PatientNotesHistoryCard } from '@/components/referrals/PatientNotesHistoryCard';

interface DieticianReferralDetailsPageProps {
  referralId?: string;
}

/**
 * Main Dietitian Referral Details Page Component
 */
export const DieticianReferralDetailsPage: React.FC<DieticianReferralDetailsPageProps> = ({
  referralId: propReferralId
}) => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const { referralId: paramReferralId } = useParams<{ referralId: string }>();
  const referralId = propReferralId || paramReferralId;

  // Use the custom hook for all non-rendering logic
  const {
    referral,
    photoUrls,
    loading,
    error,
    appointment,
    appointmentLoading,
    appointmentError,
    handleRefreshAppointment,
    retryFetchAppointment,
  } = useDieticianReferralDetails({ referralId });

  // Fetch patient meal plan assignments
  const {
    assignments: mealPlans,
    loading: mealPlansLoading,
    error: mealPlansError,
    refetch: refetchMealPlans,
    unassignMealPlan,
  } = usePatientMealPlanAssignments({
    patientId: referral?.claimedByUserId,
    params: { status: 'active' }
  });

  // Modal state (UI-specific state remains in component)
  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isAssignMealPlanDialogOpen, setIsAssignMealPlanDialogOpen] = useState(false);

  const dateOptions: Intl.DateTimeFormatOptions = {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  };

  // Handle unassign meal plan
  const handleUnassign = async (assignmentId: string) => {
    try {
      await unassignMealPlan(assignmentId);
    } catch (error) {
      console.error('Failed to unassign meal plan:', error);
      // You might want to show a toast notification here
    }
  };

  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6">
          <div className="max-w-4xl mx-auto">
            <div className="space-y-6">
              <div className="h-8 bg-neutral-300 rounded w-64 animate-pulse"></div>
              <div className="h-32 bg-neutral-300 rounded animate-pulse"></div>
              <div className="h-48 bg-neutral-300 rounded animate-pulse"></div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !referral) {
    return (
      <Layout>
        <div className="p-6">
          <div className="max-w-4xl mx-auto">
            <Card className="p-6">
              <div className="text-center space-y-4">
                <div className={`p-4 rounded-lg ${
                  isDark ? 'bg-error-900/20 text-error-400' : 'bg-error-50 text-error-600'
                }`}>
                  <BodyText>{error || 'Referral not found'}</BodyText>
                </div>
                <Button variant="outline" onClick={handleBackToDashboard}>
                  Back to Dashboard
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6">
        <div className="max-w-4xl mx-auto">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center gap-4 mb-4">
            <BackButton fallback='/dashboard'/>
          </div>

          <TitleText level={1} className="mb-2">
            Referral Details
          </TitleText>
          <BodyText variant="secondary">
            Comprehensive view of patient referral information
          </BodyText>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Patient Information */}
          <div className="lg:col-span-2 space-y-6">
            {/* Patient Details Card */}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <SubtitleText className="font-semibold">Patient Information</SubtitleText>
                <ReferralStatusBadge status={referral.status} />
              </div>

              {/* Patient Photo and Name Section */}
              <div className="flex items-center gap-4 mb-6 pb-4 border-b border-secondary-200 dark:border-secondary-700">
                <AvatarPhoto
                  src={photoUrls[referral.claimedByUserId || ''] || null}
                  firstName={referral.patientName?.split(' ')[0]}
                  lastName={referral.patientName?.split(' ').slice(1).join(' ')}
                  size="lg"
                  alt={`${referral.patientName} profile photo`}
                />
                <div>
                  <SubtitleText className="font-semibold mb-1">
                    {referral.patientName}
                  </SubtitleText>
                  <BodyText variant="secondary" size="sm">
                    Patient
                  </BodyText>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <BodyText variant="secondary" size="sm" className="font-medium mb-1">
                    Medical Conditions:
                  </BodyText>
                  <MedicalConditionTags conditions={referral.conditions} variant="compact" />
                </div>

                <div>
                  <BodyText variant="secondary" size="sm" className="font-medium mb-1">
                    Phone:
                  </BodyText>
                  <BodyText variant="muted" size="sm">
                    {PhoneUtil.formatAustralian(referral.phone)}
                  </BodyText>
                </div>

                <div>
                  <BodyText variant="secondary" size="sm" className="font-medium mb-1">
                    Referring Provider:
                  </BodyText>
                  <BodyText variant="muted" size="sm">
                    {referral.referringPhysicianRole === 'nurse' ? '': 'Dr. '}{referral.referringPhysicianName}
                  </BodyText>
                </div>

                <div>
                  <BodyText variant="secondary" size="sm" className="font-medium mb-1">
                    Referral Date:
                  </BodyText>
                  <BodyText variant="muted" size="sm">
                    {new Date(referral.createdAt).toLocaleDateString('en-AU', dateOptions)}
                  </BodyText>
                </div>

                {referral.additionalNotes && (
                  <div>
                    <BodyText variant="secondary" size="sm" className="font-medium mb-2">
                      Additional Notes:
                    </BodyText>
                    <div className={`p-3 rounded-lg ${
                      isDark ? 'bg-secondary-800' : 'bg-secondary-50'
                    }`}>
                      <BodyText variant="muted" size="sm">
                        {referral.additionalNotes}
                      </BodyText>
                    </div>
                  </div>
                )}
              </div>
            </Card>

            {/* Appointment Display - Only show if not more than 1 hour after appointment end time */}
            {(() => {
              // Check if appointment should be displayed
              if (!appointment || !appointment.endTime) {
                return (
                  <Card className="p-6">
                    <AppointmentDisplay
                      appointment={appointment}
                      isLoading={appointmentLoading}
                      error={appointmentError}
                      title="Upcoming Appointment"
                      showButtons={false}
                      onRetry={retryFetchAppointment}
                    />
                  </Card>
                );
              }

              // Calculate if current time is more than 1 hour after appointment end time
              const appointmentEndTime = new Date(appointment.endTime);
              const currentTime = new Date();
              const oneHourAfterEnd = new Date(appointmentEndTime.getTime() + (60 * 60 * 1000)); // Add 1 hour in milliseconds

              // Don't show AppointmentDisplay if current time is more than 1 hour after appointment end
              if (currentTime > oneHourAfterEnd) {
                return null;
              }

              return (
                <Card className="p-6">
                  <AppointmentDisplay
                    appointment={appointment}
                    isLoading={appointmentLoading}
                    error={appointmentError}
                    title="Upcoming Appointment"
                    showButtons={false}
                    onRetry={retryFetchAppointment}
                  />

                  {/* Additional Action Buttons for Dietitian */}
                  {appointment && appointment.eventStatus !== 'canceled' && (
                    <div className="mt-2 pt-2">
                      <div className="flex flex-wrap gap-3 justify-center">
                        {appointment.cancelUrl && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setIsCancelModalOpen(true)}
                            className="text-error-600 border-error-600 hover:bg-error-600 hover:text-white"
                          >
                            Cancel
                          </Button>
                        )}
                        {appointment.rescheduleUrl && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setIsRescheduleModalOpen(true)}
                          >
                            Reschedule
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </Card>
              );
            })()}

            {/* Appointment History Card */}
            {referral?.claimedByUserId && (
              <AppointmentHistoryCard
                dietitianId={referral.dietitianId}
                patientId={referral.claimedByUserId}
              />
            )}

            {/* Patient Notes History Card */}
            {referral?.claimedByUserId && (
              <PatientNotesHistoryCard
                dietitianId={referral.dietitianId}
                patientId={referral.claimedByUserId}
              />
            )}

          </div>

          {/* Right Column - Files */}
          <div className="lg:col-span-1 space-y-6">
            <FilesCard referralId={referral.id} />

            {/* Patient Meal Plans Section */}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <SubtitleText className="font-semibold">Meal Plans</SubtitleText>
                <div className="flex items-center gap-2">
                  {mealPlansError && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={refetchMealPlans}
                    >
                      Retry
                    </Button>
                  )}
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setIsAssignMealPlanDialogOpen(true)}
                    disabled={!referral?.claimedByUserId}
                  >
                    Assign Plan
                  </Button>
                </div>
              </div>

              {mealPlansLoading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-32 bg-neutral-300 rounded animate-pulse"></div>
                  ))}
                </div>
              ) : mealPlansError ? (
                <div className={`p-4 rounded-lg text-center ${
                  isDark ? 'bg-error-900/20 text-error-400' : 'bg-error-50 text-error-600'
                }`}>
                  <BodyText>{mealPlansError}</BodyText>
                </div>
              ) : mealPlans.length === 0 ? (
                <div className={`p-8 rounded-lg text-center ${
                  isDark ? 'bg-secondary-800' : 'bg-secondary-50'
                }`}>
                  <BodyText variant="muted">No meal plans assigned to this patient yet.</BodyText>
                </div>
              ) : (
                <div className="space-y-4">
                  {mealPlans.map((assignment) => (
                    <PatientMealPlanCard
                      key={assignment.id}
                      assignment={assignment}
                      onView={() => {
                        navigate(`/meal-plan/${assignment.mealPlan.id}`);
                      }}
                      onUnassign={handleUnassign}
                      unassignDialogTitle="Unassign Meal Plan"
                      unassignDialogMessage={`Are you sure you want to unassign "${assignment.mealPlan.label || 'this meal plan'}" from ${assignment.patientName}? This action cannot be undone.`}
                    />
                  ))}
                </div>
              )}
            </Card>
          </div>
        </div>
        </div>
      </div>

      {/* Reschedule Appointment Modal */}
      {appointment?.rescheduleUrl && (
        <RescheduleAppointmentModal
          isOpen={isRescheduleModalOpen}
          onClose={() => setIsRescheduleModalOpen(false)}
          rescheduleUrl={appointment.rescheduleUrl}
          onRefresh={handleRefreshAppointment}
        />
      )}

      {/* Cancel Appointment Modal */}
      {appointment?.cancelUrl && (
        <CancelAppointmentModal
          isOpen={isCancelModalOpen}
          onClose={() => setIsCancelModalOpen(false)}
          cancelUrl={appointment.cancelUrl}
          onRefresh={handleRefreshAppointment}
        />
      )}

      {/* Assign Meal Plan Dialog */}
      {referral?.claimedByUserId && (
        <AssignMealPlanDialog
          isOpen={isAssignMealPlanDialogOpen}
          onClose={() => setIsAssignMealPlanDialogOpen(false)}
          patientId={referral.claimedByUserId}
          patientName={referral.patientName}
          onAssignSuccess={() => {
            refetchMealPlans();
          }}
        />
      )}
    </Layout>
  );
};

export default DieticianReferralDetailsPage;